import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Bell } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { fmtRelative } from '@/lib/format';
import { useRealtime } from '@/hooks/useRealtime';
import { toast } from 'sonner';

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications?limit=20');
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // backup poll; SSE is primary
    return () => clearInterval(t);
  }, [load]);

  const browserNotify = useCallback((title, body, link) => {
    try {
      if (typeof window === 'undefined' || !('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;
      if (document.visibilityState === 'visible') return; // tab focused: toast is enough
      const n = new Notification(title || 'Zunoid CRM', {
        body: body || '',
        icon: '/icons/icon-192.png',
        tag: link || 'zunoid-notif',
      });
      n.onclick = () => {
        window.focus();
        if (link) navigate(link);
        n.close();
      };
    } catch {
      /* ignore */
    }
  }, [navigate]);

  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission === 'default') {
      // Request once per session (non-blocking)
      const key = 'zunoid_notif_asked';
      try {
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          Notification.requestPermission().catch(() => {});
        }
      } catch {
        /* ignore */
      }
    }
  }, []);

  useRealtime({
    notification: (data) => {
      load();
      if (data?.title) {
        browserNotify(data.title, data.body || '', data.link);
      }
    },
    message_in: (data) => {
      load();
      if (data?.preview) {
        toast.message('Nuevo mensaje', { description: data.preview });
        browserNotify('Nuevo mensaje', data.preview, data.lead_id ? `/app/inbox?lead=${data.lead_id}` : '/app/inbox');
      }
    },
  });

  const openOne = async (n) => {
    try {
      if (!n.read) await api.post(`/notifications/${n.id}/read`);
    } catch {
      /* ignore */
    }
    if (n.link) navigate(n.link);
    load();
  };

  const markAll = async () => {
    try {
      await api.post('/notifications/read-all');
      load();
    } catch {
      /* ignore */
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="notif-bell" className="btn-ghost h-9 w-9 p-0 relative" aria-label="Notificaciones">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[hsl(var(--danger))] text-white text-[10px] font-bold grid place-items-center">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notificaciones</span>
          {unread > 0 && (
            <button type="button" onClick={markAll} className="text-[11px] text-primary font-normal hover:underline">
              Marcar todas
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 && (
          <div className="px-3 py-6 text-xs text-muted-foreground text-center">Sin notificaciones</div>
        )}
        {items.map((n) => (
          <DropdownMenuItem
            key={n.id}
            onClick={() => openOne(n)}
            className={`flex flex-col items-start gap-0.5 py-2 cursor-pointer ${!n.read ? 'bg-accent/50' : ''}`}
          >
            <div className="flex items-center gap-1.5 w-full">
              {n.kind && (
                <span
                  className={`h-1.5 w-1.5 rounded-full shrink-0 ${
                    n.kind === 'success' ? 'bg-[hsl(var(--success))]'
                      : n.kind === 'warning' ? 'bg-[hsl(var(--warning))]'
                        : n.kind === 'message' ? 'bg-primary'
                          : n.kind === 'mention' ? 'bg-[hsl(var(--chart-2))]'
                            : 'bg-muted-foreground'
                  }`}
                />
              )}
              <div className="text-sm font-medium leading-snug flex-1">{n.title}</div>
            </div>
            {n.body && <div className="text-xs text-muted-foreground line-clamp-2 pl-3">{n.body}</div>}
            <div className="text-[10px] text-muted-foreground pl-3">{fmtRelative(n.created_at)}</div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
